import { combineReducers, configureStore } from '@reduxjs/toolkit';
import activityLogReducer from 'src/redux/activity-log/slice';
import calendarSettingsReducer from 'src/redux/calendar-settings/slice';
import chatReducer from 'src/redux/chat/slice';
import externalConnectionsReducer from 'src/redux/external-connections/slice';
import filterReducer from 'src/redux/filter/slice';
import issuesReducer from 'src/redux/issues/slice';
import listViewReducer from 'src/redux/list-view/slice';
import lookUpSliceReducer from 'src/redux/look-up/slice';
import lumiConversationReducer from 'src/redux/lumi-conversation/slice';
import maintenanceReducer from 'src/redux/maintenance/slice';
import marketsReducer from 'src/redux/markets/slice';
import modalReducer from 'src/redux/modals/slice';
import profileReducer from 'src/redux/profile/slice';
import propertiesReducer from 'src/redux/properties/slice';
import qualityCheckReducer from 'src/redux/quality-check/slice';
import quotesReducer from 'src/redux/quotes/slice';
import schedulingReducer from 'src/redux/scheduling/slice';
import { serializableCheckConfig } from 'src/redux/serializableCheckConfig';
import serviceRequestReducer from 'src/redux/service-request/slice';
import settingsUsersReducer, { roleManagementReducer } from 'src/redux/settings-users/slice';
import statusReducer from 'src/redux/status/slice';
import userManagementReducer from 'src/redux/user-management/slice';
import vModalReducer from 'src/redux/vendor-portal/modals/slice';
import onboardingReducer from 'src/redux/vendor-portal/onboarding/slice';
import requestReducer from 'src/redux/vendor-portal/request/slice';
import sidePanelReducer from 'src/redux/vendor-portal/sidebar/slice';
import vWorkOrderReducer from 'src/redux/vendor-portal/work-order/slice';
import vendorPreferencesReducer from 'src/redux/vendor-preferences/slice';
import vendorReducer from 'src/redux/vendors/slice';
import workOrderReducer from 'src/redux/work-order/slice';

// RTK enables the Redux DevTools bridge whenever `devTools` is truthy — passing an
// options object unconditionally leaks store state to the extension in production.
//
// Returns `false` when NODE_ENV is 'production', which in practice means every DEPLOYED
// environment: CI builds one `next build` artifact with NODE_ENV=production and CD only
// re-tags it, so development/qa/staging/demo all ship with the bridge off. Only
// `next dev` and jest (NODE_ENV=test) keep it on, where the custom `set` serialization
// is what makes Sets readable in the extension.
export const resolveDevTools = (
  nodeEnv = process.env.NODE_ENV,
): false | { serialize: { options: { set: true } } } =>
  nodeEnv !== 'production' && {
    serialize: {
      options: {
        set: true,
      },
    },
  };

const vendorPortalReducer = combineReducers({
  modals: vModalReducer,
  onboarding: onboardingReducer,
  requests: requestReducer,
  'work-orders': vWorkOrderReducer,
});

export const store = configureStore({
  reducer: {
    profile: profileReducer,
    modals: modalReducer,
    user_management: userManagementReducer,
    role_management: roleManagementReducer,
    filters: filterReducer,
    list_view: listViewReducer,
    calendar_settings: calendarSettingsReducer,
    vendors: vendorReducer,
    work_orders: workOrderReducer,
    lookUp: lookUpSliceReducer,
    service_requests: serviceRequestReducer,
    quotes: quotesReducer,
    properties: propertiesReducer,
    scheduling: schedulingReducer,
    status: statusReducer,
    vendor: vendorPortalReducer,
    issues: issuesReducer,
    quality_check: qualityCheckReducer,
    counters: sidePanelReducer,
    chat: chatReducer,
    activity_log: activityLogReducer,
    external_connections: externalConnectionsReducer,
    lumi_conversation: lumiConversationReducer,
    markets: marketsReducer,
    vendor_preferences: vendorPreferencesReducer,
    settings_users: settingsUsersReducer,
    maintenance: maintenanceReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: serializableCheckConfig,
    }),
  devTools: resolveDevTools(),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
